import axios from "axios";
// config
import { HOST_API } from "src/config-global";
//redux for access
import { setReadAccess, setWriteAccess } from "src/redux/slices/access";
// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.request.use(
  function (config) {
    if (config.data instanceof FormData) {
      // Let the browser auto-set Content-Type with the correct multipart boundary.
      // Setting it manually omits the boundary and breaks multer on the server.
      config.headers.delete('Content-Type');
    } else {
      config.headers["Content-Type"] = "application/json";
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) =>{
    return Promise.reject(
      (error.response && error.response.data) || "Something went wrong"
    )
  }
);

export const axiosError = (dispatch, error, dispatchFunct)=>{
    const {read_access, write_access} = error || undefined;
    if(read_access!= undefined && read_access == false){
      dispatch(setReadAccess(false));
    }
    if(write_access != undefined && write_access == false){
      dispatch(setWriteAccess(false));
    }
    dispatchFunct.map(func=>{
      dispatch(func(error))
    })
}

export default axiosInstance;

export const API_ENDPOINTS = {
  geo:{
    country:"/api/geo/country",
    state:"/api/geo/state",
    city:"/api/geo/city",
    search:"/api/geo/search"
  },
  auth: {
    me: "/api/admin/me",
    login: "/api/admin/login",
    register: "/api/admin/register",
    update: "api/admin/account/update"
  },
  dashboard: {
    stats: "/api/admin/dashboard/stats",
  },
  notifications: {
    list: "/api/admin/notifications",
    unreadCount: "/api/admin/notifications/unread-count",
    detail: (id) => `/api/admin/notifications/${id}`,
    markAllRead: "/api/admin/notifications/read-all",
    markRead: (id) => `/api/admin/notifications/${id}/read`,
  },
  ai: {
    users: "/api/admin/ai/users",
    agentFailures: "/api/admin/agent/failures",
  },
  campaigns: {
    list: "/api/admin/campaigns",
    email: "/api/admin/campaigns/email",
    whatsapp: "/api/admin/campaigns/whatsapp",
    status: (jobId) => `/api/admin/campaigns/${jobId}/status`,
  },
  representationAgreements: {
    list: "/api/talent-representation-agreements",
    detail: (id) => `/api/talent-representation-agreements/${id}`,
  },
  user: {
    list: "/api/admin/admins",
    details: "/api/admin/admins/{id}",
    add: "/api/admin/admins",
    promote: "/api/admin/admins/promote",
    edit: "/api/admin/admins/{id}",
    delete: "/api/admin/admins",
    verifyEmail: "/api/admin/admins/{id}/verify-email"
  },
  account:{
    general:"/api/profile",
    workspace:{
      get:"/api/profile/my-organization-profile",
      update:"/api/profile/organization"
    },
    change_password: "/api/profile/password",
    notifications: "/api/profile/notification-preferences",
    update_notifications: "/api/profile/notification-preferences",
    search_profiles: "/api/admin/profiles/search"
  },
  role: {
   list: "/api/admin/user/roles",
   permissions: "/api/admin/user/role-permissions",
   add: "/api/admin/user/add-role",
   edit: "/api/admin/user/edit-role",
  },
  rolePermissions : "/api/admin/role-permissions",
  checkpermission: "/api/admin/check-permissions",
  jobs:{
      list:"/api/admin/jobs",
      applicants:"/api/jobs/my-posts/{id}/applicants",
      create:"/api/admin/job",
      uploadImages:(id) =>`/api/admin/job/${id}/upload-images`,
      roles:"/api/admin/job/{id}/roles",
      get:(id) =>`/api/admin/jobs/${id}`
  },
  talent:{
    list:"/api/admin/talents",
    types:"/api/admin/talent-types",
    edit:(id) =>`/api/admin/talents/${id}`,
    delete:(id) =>`/api/admin/talents/${id}`,
    mediaUpdate:(id) =>`/api/admin/talents/${id}/media`,
    mediaReorder:(id) =>`/api/admin/talents/${id}/media/reorder`,
    mediaPolaroid:(id, mediaId) =>`/api/admin/talents/${id}/media/${mediaId}/polaroid`,
    hired:"/api/admin/hired-talents",
    profile:(short_url) =>`/api/talents/${short_url}`,
    languages:"/api/languages",
    createProfile: (id) => `/api/admin/talents/${id}/profile`,
    create: (id) => `/api/admin/talent/${id}`,
    resetPassword: (id) => `/api/admin/talents/${id}/reset-password`,
    verifyEmail: (id) => `/api/admin/talents/${id}/verify-email`,
    sendResetPasswordLink: (id) => `/api/admin/talents/${id}/send-reset-password-link`,
    convertToAgency: (id) => `/api/admin/talents/${id}/convert-to-agency`,
    archetypes: "/api/archetypes",
    archetype: (n) => `/api/archetypes/${n}`,
    portfolio: (id) => `/api/admin/talents/${id}/portfolio`,
    portfolioRawMedia: (id) => `/api/admin/talents/${id}/portfolio/raw-media`,
    portfolioReorder: (id) => `/api/admin/talents/${id}/portfolio/reorder`,
    portfolioMedia: (id, mediaId) => `/api/admin/talents/${id}/portfolio/media/${mediaId}`,
    portfolioVisibility: (id) => `/api/admin/talents/${id}/portfolio/visibility`,
    portfolioItem: (id, itemId) => `/api/admin/talents/${id}/portfolio/${itemId}`,
  },
  talentOffers: {
    list: "/api/admin/talent-offers",
  },
  chats: {
    list: "/api/admin/chats",
    messages: (userAId, userBId) => `/api/admin/chats/${userAId}/${userBId}/messages`,
  },
  applications:{
    list:"/api/admin/jobs/applications",
    updateStatus:(jobId, id) => `/api/admin/jobs/${jobId}/applications/${id}/status`
  },
  company:{
    agencies:"/api/admin/agencies",
    organizations:"/api/admin/organizations",
    edit:(id) =>`/api/admin/companies/${id}`,
    delete:(id) =>`/api/admin/companies/${id}`,
    company:(id) =>`/api/admin/companies/${id}/company`,
    notifications:(id) =>`/api/admin/companies/${id}/notification-preferences`,
    resetPassword:(id) =>`/api/admin/companies/${id}/reset-password`,
    resetUserPassword:(id) =>`/api/admin/companies/${id}/reset-user-password`,
    sendResetPasswordLink:(id) =>`/api/admin/companies/${id}/send-reset-password-link`,
    searchProfiles:(query) =>`/api/admin/profiles/search?query=${query}`
  },
  agency:{
    crm:(id) =>`/api/admin/agencies/${id}/crm`,
    permissions:(id) =>`/api/admin/agencies/${id}/permissions`,
    convertToTalent:(id) =>`/api/admin/agencies/${id}/convert-to-talent`,
  },
  whatsapp:{
    chats:"/api/admin/whatsapp/chats",
    messages:(sessionId) =>`/api/admin/whatsapp/chats/${sessionId}/messages`,
    reply:(sessionId) =>`/api/admin/whatsapp/chats/${sessionId}/reply`,
    improveChat:(sessionId) =>`/api/admin/whatsapp/chats/${sessionId}/improve-chat`,
    templates:"/api/admin/whatsapp/templates",
    templatesSync:"/api/admin/whatsapp/templates/sync",
    campaign:"/api/admin/whatsapp/campaign",
    campaigns:"/api/admin/whatsapp/campaigns",
    campaignDetail:(id) =>`/api/admin/whatsapp/campaigns/${id}`,
    campaignAnalytics:(id) =>`/api/admin/whatsapp/campaigns/${id}/analytics`,
    campaignOverallAnalytics: "/api/admin/whatsapp/campaigns/analytics/overall",
  },
  leads:{
    list:"/api/leads",
    create:"/api/leads",
    details:(id) =>`/api/leads/${id}`,
    verifyEmail:(id) =>`/api/leads/${id}/verify-email`,
    resetPassword:(id) =>`/api/leads/${id}/reset-password`,
    sendResetPasswordLink:(id) =>`/api/leads/${id}/send-reset-password-link`,
    convertToAgency:(id) =>`/api/leads/${id}/convert-to-agency`,
    sendWhatsapp:(id) =>`/api/leads/${id}/send-whatsapp`
  },
  proposals:{
    folders:"/api/admin/proposals/folders",
  },
  proposalMisc:{
    talentDraft:(id) =>`/api/admin/proposals/misc/${id}/talent-draft`,
    attachMedia:(id) =>`/api/admin/proposals/misc/${id}/attach-media`,
  },
  locations:{
    list:"/api/admin/locations",
    types:"/api/locations/types",
    details:(id) =>`/api/admin/locations/${id}`,
    update:(id) =>`/api/admin/locations/${id}`,
  },
  proposalFolders:{
    details:(id) =>`/api/admin/proposals/folders/${id}`,
    update:(id) =>`/api/admin/proposals/folders/${id}`,
  },
  equipments:{
    list:"/api/admin/equipments",
    details:(id) =>`/api/admin/equipments/${id}`,
    update:(id) =>`/api/admin/equipments/${id}`,
  },
  auditions:{
    list:"/api/admin/auditions",
    details:(id) =>`/api/admin/auditions/${id}`,
    responsesByJobAndUser: (jobId, userId) => `/api/auditions/responses/job/${jobId}/user/${userId}`,
  },
  clients:{
    list:"/api/admin/clients",
    details:(id) =>`/api/admin/clients/${id}`,
    giveCrmAccess:(contactId) =>`/api/admin/clients/poc/${contactId}/give-crm-access`,
    revokeCrmAccess:(contactId) =>`/api/admin/clients/poc/${contactId}/revoke-crm-access`,
  },
  adminGeo:{
    countries:"/api/admin/geo/countries",
    updateCountry:(id) =>`/api/admin/geo/countries/${id}`,
    states:"/api/admin/geo/states",
    updateState:(id) =>`/api/admin/geo/states/${id}`,
    cities:"/api/admin/geo/cities",
    updateCity:(id) =>`/api/admin/geo/cities/${id}`,
    taxRates:"/api/admin/geo/tax-rates",
    updateTaxRate:(id) =>`/api/admin/geo/tax-rates/${id}`,
    serviceMasters:"/api/admin/geo/service-masters",
    currencies:"/api/admin/geo/currencies",
    updateCurrency:(id) =>`/api/admin/geo/currencies/${id}`,
  },
  adminServices:{
    list:"/api/admin/services",
    update:(id) =>`/api/admin/services/${id}`,
  },
  adminLanguages:{
    list:"/api/admin/languages",
    update:(id) =>`/api/admin/languages/${id}`,
  },
  contactForms:{
    list:"/api/admin/contact-forms",
    detail:(id) =>`/api/admin/contact-forms/${id}`,
    updateStatus:(id) =>`/api/admin/contact-forms/${id}/status`,
    reply:(id) =>`/api/admin/contact-forms/${id}/reply`,
  },
  testimonials:{
    list:"/api/admin/testimonials",
    detail:(id) =>`/api/admin/testimonials/${id}`,
    create:"/api/admin/testimonials",
    update:(id) =>`/api/admin/testimonials/${id}`,
    delete:(id) =>`/api/admin/testimonials/${id}`,
  },
  contracts:{
    list:"/api/admin/contracts",
    detail:(id) =>`/api/admin/contracts/${id}`,
  },
  sharedMedia:{
    list:"/api/admin/shared-media",
  },
  accessRequests:{
    list:"/api/admin/access-requests",
    update:(token) =>`/api/admin/access-requests/${token}`,
  },
  projects:{
    list:"/api/admin/projects",
    detail:(id) =>`/api/admin/projects/${id}`,
  },
  bookings:{
    locations:"/api/admin/bookings/locations",
    locationStatus:(id) =>`/api/admin/bookings/locations/${id}/status`,
    equipments:"/api/admin/bookings/equipments",
    equipmentStatus:(id) =>`/api/admin/bookings/equipments/${id}/status`,
    talents:"/api/admin/bookings/talents",
    talentStatus:(id) =>`/api/admin/bookings/talents/${id}/status`,
  },
  jobLeads:{
    list:"/api/job-leads",
    details:(id) =>`/api/job-leads/${id}`,
    reject:(id) =>`/api/job-leads/${id}/reject`,
    publish:(id) =>`/api/job-leads/${id}/publish`,
    companySearch:"/api/companies/search",
    profileSearch:"/api/admin/profiles/search",
  },
  brightDataScrapeJobs:{
    list:"/api/brightdata-scrape-jobs",
    create:"/api/brightdata-scrape-jobs",
    details:(id) =>`/api/brightdata-scrape-jobs/${id}`,
    refresh:(id) =>`/api/brightdata-scrape-jobs/${id}/refresh`,
  },
  agencyDiscoveryLeads:{
    list:"/api/agency-discovery-leads",
    create:"/api/agency-discovery-leads",
    details:(id) =>`/api/agency-discovery-leads/${id}`,
    refresh:(id) =>`/api/agency-discovery-leads/${id}/refresh`,
    reject:(id) =>`/api/agency-discovery-leads/${id}/reject`,
    contacted:(id) =>`/api/agency-discovery-leads/${id}/contacted`,
    retryWebsiteCrawl:(id) =>`/api/agency-discovery-leads/${id}/retry-website-crawl`,
  },
};
